import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import NumberFormat from 'react-number-format';
import { FeeConfigurationResponse } from '../../../api/ConfigApi';
import PolicyApi from '../../../api/PolicyApi';
import { useAuthContext } from '../../../context/auth';
import { CurrencyCode } from '../../../types/currencyCode';
import { FeeCreateRequest, FeeUpdateRequest } from '../../../types/policy';
import { getCurrencyPrefix } from '../../../util/util';
import { errorToast } from '../../Toast';

interface Props {
	authToken: string;
	policyLocator: string;
	policyStartTimestamp: string;
	effectiveContractEndTimestamp: string;
	currency: CurrencyCode;
	feeOptions: FeeConfigurationResponse[];
	updateFeesTable(): void;
}

interface FormValues {
	name: string;
	amount: string;
	description: string;
}

interface FormErrors {
	name?: string;
	amount?: string;
	description?: string;
}

interface FormTouched {
	name: boolean;
	amount: boolean;
	description: boolean;
}

const useStyles = makeStyles((theme) => ({
	field: {
		width: '48%',
	},
	description: {
		margin: theme.spacing(2, 0, 2, 0),
	},
	modalForm: {
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'space-between',
	},
}));

const AddFeeModal = ({
	policyStartTimestamp,
	effectiveContractEndTimestamp,
	feeOptions,
	currency,
	authToken,
	policyLocator,
	updateFeesTable,
}: Props) => {
	const authData = useAuthContext();
	const classes = useStyles();
	const [open, setOpen] = React.useState(false);
	const [submitting, setSubmitting] = React.useState(false);
	const hasPermission = authData.hasPermission;

	const [values, setValues] = React.useState<FormValues>({
		name: '',
		amount: '',
		description: '',
	});

	const [errors, setErrors] = React.useState<FormErrors>({});

	const [touched, setTouched] = React.useState<FormTouched>({
		name: false,
		amount: false,
		description: false,
	});

	React.useEffect(() => {
		if (open) {
			setValues({
				name: '',
				amount: '',
				description: '',
			});
			setTouched({
				name: false,
				amount: false,
				description: false,
			});
			setSubmitting(false);
			setErrors({});
		}
	}, [open]);

	const handleOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const touchAndValidate = (
		fieldName: keyof FormValues,
		value: string,
		existingTouched?: FormTouched,
		existingErrors?: FormErrors,
	) => {
		const newTouched: FormTouched = existingTouched
			? existingTouched
			: { ...touched };
		const newErrors: FormErrors = existingErrors
			? existingErrors
			: { ...errors };

		newTouched[fieldName] = true;

		if (value === '' && fieldName !== 'description') {
			newErrors[fieldName] = 'Enter a value';
		} else {
			delete newErrors[fieldName];
		}

		return { newTouched, newErrors };
	};

	const addFee = async () => {
		const parsedPolicyEndDate = parseInt(effectiveContractEndTimestamp, 10);
		const parsedPolicyStartDate = parseInt(policyStartTimestamp, 10);
		let startTimestamp;
		let endTimestamp;
		const today = new Date().valueOf();

		if (today < parsedPolicyStartDate) {
			startTimestamp = parsedPolicyStartDate;
			endTimestamp = parsedPolicyStartDate + 1;
		} else if (today > parsedPolicyEndDate) {
			startTimestamp = parsedPolicyEndDate - 1;
			endTimestamp = parsedPolicyEndDate;
		} else {
			startTimestamp = today;
			endTimestamp = today + 1;
		}

		const fee: FeeCreateRequest = {
			feeName: values.name,
			description: values.description,
			amount: parseFloat(values.amount),
			startTimestamp: '' + startTimestamp,
			endTimestamp: '' + endTimestamp,
		};

		const feeRequest: FeeUpdateRequest = {
			newFees: [fee],
		};

		try {
			const response = await PolicyApi.addFee(
				authToken,
				policyLocator,
				feeRequest,
			);
			if (response.status === 200) {
				setOpen(false);
				// Empty form
				setValues({
					name: '',
					amount: '',
					description: '',
				});
				// Reset touched
				setTouched({
					name: false,
					amount: false,
					description: false,
				});
				updateFeesTable();
			}
		} catch (e) {
			errorToast(e.message);
		} finally {
			setSubmitting(false);
		}
	};

	const handleSubmit = async (e: React.SyntheticEvent) => {
		e.preventDefault();
		setSubmitting(true);
		const newTouched = { ...touched };
		const newErrors = { ...errors };
		Object.keys(values).forEach((key: keyof FormValues) => {
			touchAndValidate(key, values[key], newTouched, newErrors);
		});
		setTouched(newTouched);
		setErrors(newErrors);

		if (Object.keys(newErrors).length === 0) {
			await addFee();
		} else {
			setSubmitting(false);
		}
	};

	const handleBlur = (name: keyof FormValues, value: string) => {
		const { newTouched, newErrors } = touchAndValidate(name, value);
		setTouched(newTouched);
		setErrors(newErrors);
	};

	const handleChange = (name: keyof FormValues, value: string) => {
		const newValues = { ...values };

		const { newTouched, newErrors } = touchAndValidate(name, value);
		newValues[name] = value;

		setValues(newValues);
		setTouched(newTouched);
		setErrors(newErrors);
	};

	return (
		<>
			{hasPermission('policy.fee') && (
				<Button variant='outlined' onClick={() => handleOpen()}>
					Add Fee
				</Button>
			)}
			<Dialog
				fullWidth
				open={open}
				onClose={handleClose}
				aria-labelledby='add-fee-title'
			>
				<DialogTitle id='add-fee-title'>Add Fee</DialogTitle>
				<form onSubmit={handleSubmit}>
					<DialogContent className={classes.modalForm}>
						<TextField
							fullWidth
							select
							SelectProps={{
								native: true,
							}}
							id='name'
							name='name'
							label='Name'
							error={!!errors.name && !!touched.name}
							helperText={touched.name && errors.name}
							value={values.name}
							onChange={(e) => handleChange('name', e.target.value)}
							onBlur={(e) =>
								handleBlur(e.target.name as keyof FormValues, e.target.value)
							}
							className={classes.field}
							disabled={submitting}
						>
							<option aria-label='None' value='' />
							{feeOptions &&
								feeOptions.map((fee, index) => (
									<option key={index} value={fee.name}>
										{fee.displayName}
									</option>
								))}
						</TextField>
						<NumberFormat
							fullWidth
							id='amount'
							name='amount'
							label='Amount'
							error={!!errors.amount && !!touched.amount}
							helperText={touched.amount && errors.amount}
							value={values.amount}
							className={classes.field}
							customInput={TextField}
							onBlur={(e) =>
								handleBlur(e.target.name as keyof FormValues, e.target.value)
							}
							prefix={getCurrencyPrefix(currency)}
							onValueChange={({ value }) => handleChange('amount', value)}
							type='text'
							thousandSeparator
							decimalScale={2}
							disabled={submitting}
						/>
						<TextField
							fullWidth
							id='description'
							name='description'
							label='Description'
							error={!!errors.description && !!touched.description}
							helperText={touched.description && errors.description}
							value={values.description}
							onChange={(e) => handleChange('description', e.target.value)}
							onBlur={(e) =>
								handleBlur(e.target.name as keyof FormValues, e.target.value)
							}
							className={classes.description}
							disabled={submitting}
						/>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={handleClose}
							color='primary'
							variant='contained'
							disabled={submitting}
						>
							Cancel
						</Button>
						<Button
							type='submit'
							color='primary'
							variant='contained'
							disabled={submitting}
						>
							Add
						</Button>
					</DialogActions>
				</form>
			</Dialog>
		</>
	);
};

export default AddFeeModal;
